import { Fragment, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApplications, useGrowth, useUsage } from '@/hooks/useConsoleData';
import { Section } from '@/components/console/Section';
import { PAID_SOURCE_LABEL, PersonCard } from '@/components/console/PersonCard';
import { JournalPanel } from '@/components/console/JournalPanel';
import { APP_STATUS_LABEL, decideApplication, fetchCredentialUrl, type GrowthPerson, type ProviderApplicationRow } from '@/lib/admin-data';
import { sortWarmFirst, warmth, writeFirstCount, WARMTH_LABEL } from '@/lib/growth-order';
import { money, num, when } from '@/lib/format';
import { FunnelPanel } from '@/components/console/FunnelPanel';
import { PeopleTable } from '@/components/console/PeopleTable';
import { peopleLinkString, peopleQueryFromParams, type PeopleQuery } from '@/lib/people-data';
import s from './console.module.css';

/**
 * People (Brian, 2026-09-18): "so I can see exactly who's using it, how I
 * can reengage with them, find out what they need, find out where they
 * don't advance." The funnel first (where people stop), then everyone in
 * one table with filters and sort, then the warm-first cards for writing
 * to recent signups, payments, and provider applications behind drop-downs.
 * The Journal opens in a side panel; a name opens the person's own page.
 * Every number is a row count from the API.
 */
const FILTERS: { key: 'all' | GrowthPerson['state']; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'paid', label: 'Paid' },
  { key: 'abandoned_checkout', label: 'Went to checkout, not paid' },
  { key: 'checked', label: 'Did a check' },
  { key: 'signed_up', label: 'No check yet' },
  { key: 'unverified', label: 'Not verified' },
];

export default function PeoplePage() {
  const [params, setParams] = useSearchParams();
  const focus = params.get('user');
  const journalOpen = params.get('journal') === '1' && !!focus;
  const [filter, setFilter] = useState<'all' | GrowthPerson['state']>('all');
  const [q, setQ] = useState('');
  const qc = useQueryClient();
  const growth = useGrowth();
  const g = growth.data;
  // The table's filters live in the URL so a filtered view is a link (the funnel rows link here with ?stage=).
  const peopleQuery = useMemo(() => peopleQueryFromParams(params), [params]);
  const setPeopleQuery = (next: PeopleQuery) => {
    const keep = new URLSearchParams(peopleLinkString(next));
    for (const k of ['user', 'journal', 'section']) if (params.get(k)) keep.set(k, params.get(k)!);
    setParams(keep, { replace: true });
  };

  const shown = useMemo(() => {
    if (!g) return [];
    const needle = q.trim().toLowerCase();
    return sortWarmFirst(
      g.recentSignups.filter((p) => (filter === 'all' || p.state === filter) && (!needle || `${p.name} ${p.email} ${p.phone ?? ''}`.toLowerCase().includes(needle))),
    );
  }, [g, filter, q]);
  const usage = useUsage(shown.map((p) => p.userId));
  const writeFirst = writeFirstCount(shown);
  // The newest payment row per person, so a card can say what they actually paid.
  const paymentByUser = useMemo(() => {
    const out = new Map<string, (typeof g extends undefined ? never : NonNullable<typeof g>)['recentPayments'][number]>();
    for (const pay of g?.recentPayments ?? []) if (!out.has(pay.userId)) out.set(pay.userId, pay);
    return out;
  }, [g]);

  const openJournal = (userId: string) => {
    const next = new URLSearchParams(params);
    next.set('user', userId);
    next.set('journal', '1');
    setParams(next, { replace: false });
  };
  const closeJournal = () => {
    const next = new URLSearchParams(params);
    next.delete('journal');
    setParams(next, { replace: true });
  };
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['growth'] }); void qc.invalidateQueries({ queryKey: ['messages'] }); };

  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>People</h1>
        <span className={s.pageSub}>{g ? `${num(g.allTime.signups)} accounts · ${num(g.allTime.activeSubscriptions)} paying now · refreshed ${when(g.generatedAt)}` : ''}</span>
      </div>

      {growth.isError && <div className={s.error}>Could not load the people. This page needs GET /provider/admin/growth.</div>}
      {g && (
        <div className={s.strip}>
          {(['today', 'last7d', 'allTime'] as const).map((k) => (
            <div key={k} className={s.stat}>
              <span className={s.statLabel}>{k === 'today' ? 'Today' : k === 'last7d' ? 'Last 7 days' : 'All time'}</span>
              <span className={s.statVal}>{num(g[k].signups)}</span>
              <span className={s.statSub}>{num(g[k].verified)} verified · {num(g[k].firstChecks)} first checks · {num(g[k].paid)} paid</span>
            </div>
          ))}
          <div className={s.stat}>
            <span className={s.statLabel}>Paying now</span>
            <span className={s.statVal}>{num(g.allTime.activeSubscriptions)}</span>
            <span className={s.statSub}>active subscriptions</span>
          </div>
        </div>
      )}

      <FunnelPanel />

      <Section title="Everyone" summary="one row per person: stage, where they are stuck and why, plan, last seen, streak, checks, Active Mode, platforms, what Elina knows, open asks" count={g ? num(g.allTime.signups) : null} defaultOpen id="everyone">
        <PeopleTable query={peopleQuery} onChange={setPeopleQuery} />
      </Section>

      <div className={`${s.twoCol} ${journalOpen ? s.twoColOpen : ''}`}>
        <div className={s.stack}>
          <Section title="Write to these first" summary="the last 100 signups, warm first, with the note Brian would send" count={g ? g.recentSignups.length : null} defaultOpen={!!focus || params.get('section') === 'write'} id="write">
          <div className={s.stack}>
          <div className={s.filters} role="tablist" aria-label="Filter accounts">
            {FILTERS.map((f) => {
              const n = g ? (f.key === 'all' ? g.recentSignups.length : g.recentSignups.filter((p) => p.state === f.key).length) : 0;
              return (
                <button key={f.key} type="button" role="tab" aria-selected={filter === f.key} className={`${s.filter} ${filter === f.key ? s.filterOn : ''}`} onClick={() => setFilter(f.key)}>
                  {f.label} <span className={s.filterCount}>{n}</span>
                </button>
              );
            })}
            <input className={s.search} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone" aria-label="Search people" />
          </div>
          <div className={s.people}>
            {g && shown.length === 0 && <div className={s.empty}>Nobody in this group yet.</div>}
            {shown.map((p, i) => (
              <Fragment key={p.userId}>
                {i === 0 && writeFirst > 0 && (
                  <div className={s.writeFirst}>
                    <span className={s.writeFirstLabel}>Write to these first</span>
                    <span className={s.sub}>{WARMTH_LABEL[warmth(shown[0])]} · {writeFirst}</span>
                  </div>
                )}
                <PersonCard p={p} usage={usage.data ? usage.data.people[p.userId] ?? null : usage.isError ? null : undefined} payment={paymentByUser.get(p.userId) ?? null} open={p.userId === focus} onSent={refresh} onJournal={openJournal} />
              </Fragment>
            ))}
          </div>
          </div>
          </Section>

          {g && (
            <Section title="Payments" summary="what each person actually paid, from the Stripe invoice" count={g.recentPayments.length}>
              <div className={s.tableWrap}>
                <table className={s.table}>
                  <thead><tr><th>When</th><th>Who</th><th>Signed up</th><th>First check</th><th>Checks</th><th>Plan</th><th>Paid</th><th>Read from</th><th>List price</th><th>Code</th><th>Status</th><th>Renews</th><th></th></tr></thead>
                  <tbody>
                    {g.recentPayments.length === 0 && <tr><td colSpan={13}>No payments yet.</td></tr>}
                    {g.recentPayments.map((p) => {
                      const person = g.recentSignups.find((x) => x.userId === p.userId);
                      return (
                        <tr key={p.userId + p.paidAt}>
                          <td>{when(p.paidAt)}</td>
                          <td>{p.name || p.email}<div className={s.sub}>{p.email}</div></td>
                          <td>{when(person?.signupAt ?? person?.createdAt ?? null, 'not in the recent list')}</td>
                          <td>{person ? when(person.firstCheckAt, 'no check yet') : 'not in the recent list'}</td>
                          <td>{person ? num(person.checks) : 'not in the recent list'}</td>
                          <td>{p.plan}{p.interval ? ` · ${p.interval}` : ''}</td>
                          <td><strong>{p.paidUsd === null ? 'not measured' : money(p.paidUsd)}</strong></td>
                          <td className={s.sub}>{p.paidSource ? PAID_SOURCE_LABEL[p.paidSource] : p.paidUsd === null ? 'Stripe showed no payment' : 'not recorded'}</td>
                          <td>{p.priceUsd === null ? 'not recorded' : money(p.priceUsd)}</td>
                          <td>{p.coupon ?? ''}</td>
                          <td>{p.status}</td>
                          <td>{when(p.periodEnd)}</td>
                          <td>{p.ghlUrl ? <a href={p.ghlUrl} target="_blank" rel="noreferrer">GHL</a> : ''}{person && <> · <button type="button" className={s.linkBtn} onClick={() => openJournal(p.userId)}>Journal</button></>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          <Applications defaultOpen={params.get('section') === 'applications'} />
        </div>

        {journalOpen && focus && <JournalPanel userId={focus} onClose={closeJournal} />}
      </div>
    </div>
  );
}

/** Practitioner applications: the review queue, approve or reject with a reason. */
function Applications({ defaultOpen }: { defaultOpen: boolean }) {
  const apps = useApplications();
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<ProviderApplicationRow | null>(null);
  const [reason, setReason] = useState('');
  const rows = apps.data ?? [];
  const pending = rows.filter((r) => r.status === 'under_review').length;

  const decide = async (id: string, approve: boolean) => {
    setBusyId(id);
    try {
      await decideApplication(id, approve, approve ? undefined : reason.trim());
      setRejecting(null);
      setReason('');
      void qc.invalidateQueries({ queryKey: ['applications'] });
    } catch {
      window.alert('That did not go through. Try again.');
    } finally {
      setBusyId(null);
    }
  };
  const viewCredential = async (id: string) => {
    const url = await fetchCredentialUrl(id).catch(() => null);
    if (url) window.open(url, '_blank', 'noopener');
    else window.alert('No credential document on this application.');
  };

  return (
    <Section title="Provider applications" summary={apps.isError ? 'did not load' : `${pending} waiting for a decision`} count={rows.length} defaultOpen={defaultOpen || pending > 0}>
      {apps.isError && <div className={s.error}>Could not load applications.</div>}
      {apps.data && rows.length === 0 && <div className={s.empty}>No applications yet.</div>}
      <div className={s.list}>
        {rows.map((r) => (
          <details key={r.id} className={s.msg}>
            <summary className={s.msgHead}>
              <span className={`${s.pill} ${r.status === 'under_review' ? s.pill_new : r.status === 'rejected' || r.status === 'expired' ? s.pill_failed : s.pill_sent}`}>{APP_STATUS_LABEL[r.status]}</span>
              <strong>{r.practiceName}</strong>
              <span className={s.sub}>{r.applicantName} · {r.practiceType} · {r.email}{r.phone ? ` · ${r.phone}` : ''} · {r.submittedAt}</span>
            </summary>
            <div className={s.msgBody}>
              <dl className={s.facts}>
                <dt>License</dt><dd>{r.credentialNumber}{r.credentialFileName ? <> · <button type="button" className={s.linkBtn} onClick={() => void viewCredential(r.id)}>{r.credentialFileName}</button></> : ''}</dd>
                <dt>Patient volume</dt><dd>{r.detail.intake.patientVolume ?? 'not given'}</dd>
                <dt>Intended use</dt><dd>{r.detail.intake.intendedUse.length ? r.detail.intake.intendedUse.join(', ') : 'not given'}</dd>
                {r.detail.intake.customInterest && <><dt>Customization</dt><dd>{r.detail.intake.customInterest}</dd></>}
                {r.detail.intake.notes && <><dt>Notes</dt><dd>{r.detail.intake.notes}</dd></>}
                {r.detail.rejectionReason && <><dt>Rejection reason</dt><dd>{r.detail.rejectionReason}</dd></>}
                {r.detail.payWindowEndsAtIso && <><dt>Pay window ends</dt><dd>{when(r.detail.payWindowEndsAtIso)}</dd></>}
              </dl>
              {r.status === 'under_review' && (
                <div className={s.draftActions} style={{ marginTop: 10 }}>
                  <button type="button" className={s.btn} disabled={busyId === r.id} onClick={() => void decide(r.id, true)}>Approve</button>
                  {rejecting?.id === r.id ? (
                    <>
                      <input className={s.draftSubject} style={{ flex: '1 1 240px' }} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason, emailed to the applicant" />
                      <button type="button" className={s.btnGhost} disabled={busyId === r.id || !reason.trim()} onClick={() => void decide(r.id, false)}>Reject and notify</button>
                      <button type="button" className={s.linkBtn} onClick={() => setRejecting(null)}>Cancel</button>
                    </>
                  ) : (
                    <button type="button" className={s.btnGhost} disabled={busyId === r.id} onClick={() => setRejecting(r)}>Reject</button>
                  )}
                </div>
              )}
            </div>
          </details>
        ))}
      </div>
    </Section>
  );
}
